import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { Table, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// icons
import { EyeOff, Eye, Edit, Trash2 } from 'lucide-react';
// components
import { CopyButton, PopoverButton } from '@newo/ui-kit';
import type { ILLMKey } from '@newo/api/gateway';
import LLMKeyForm from '@/pages/Profile/components/LLMKeys/components/LLMKeyForm';
// utils
import {
    QUERY_KEYS,
    useDeleteKeyMutation
} from '@/services/customer/providers/providers.queries.ts';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';

interface LLMKeyProps {
    llmKey: ILLMKey;
    hasMainKey?: boolean;
}

const LLMKey = ({ llmKey, hasMainKey }: LLMKeyProps) => {
    const queryClient = useQueryClient();
    const [isVisible, { toggle }] = useDisclosure(false);
    const deleteKeyMutation = useDeleteKeyMutation();
    const handleDelete = () => {
        openConfirmDeleteModal({
            title: 'Delete LLM Key?',
            children: 'Are you sure you want to delete LLM Key?',
            confirmLabel: 'Delete LLM Key',
            onConfirm: () => {
                deleteKeyMutation.mutate(
                    { keyId: llmKey.id },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey: QUERY_KEYS.getLLMProviderKeys(
                                    llmKey.provider_id
                                )
                            });
                        }
                    }
                );
            }
        });
    };

    const key = isVisible ? llmKey.key : llmKey.key.replaceAll(/./g, '*');

    const roleClassName =
        llmKey.role === 'main' ? 'text-green-6' : 'text-gray-6';

    return (
        <Table.Tr>
            <Table.Td>
                <div className="flex gap-4">
                    <div className="break-all">{key}</div>
                    <UnstyledButton
                        className="ml-auto text-gray-6"
                        onClick={toggle}
                    >
                        {isVisible ? <Eye /> : <EyeOff />}
                    </UnstyledButton>
                    <CopyButton
                        className="leading-normal text-gray-6"
                        value={llmKey.key}
                    />
                </div>
            </Table.Td>
            <Table.Td className={clsx('capitalize', roleClassName)}>
                {llmKey.role}
            </Table.Td>
            <Table.Td>{llmKey.priority}</Table.Td>
            <Table.Td>
                <div className="flex justify-end gap-4 text-gray-6">
                    <PopoverButton trigger={<Edit />}>
                        {(close) => (
                            <LLMKeyForm
                                hasMainKey={hasMainKey}
                                llmKey={llmKey}
                                providerId={llmKey.provider_id}
                                onClose={close}
                            />
                        )}
                    </PopoverButton>
                    <UnstyledButton onClick={handleDelete}>
                        <Trash2 />
                    </UnstyledButton>
                </div>
            </Table.Td>
        </Table.Tr>
    );
};

export default LLMKey;
